/**
 * This function generates an array of unique random colours that are colourblind accessible for use in analytics
 * charts.
 *
 * @param numColours The number of unique colours to generate
 */
const generateColours = (numColours: number): string[] => {
  const primaryBlue = '#057D99';
  const primaryOrange = '#f7a621';

  const baseColours = [
    primaryBlue, // Brand blue
    primaryOrange, // Brand orange
    '#012438', // Brand blue (darker)
    '#9B4393', // Purple
    '#2AA876', // Teal green
    '#E74C3C', // Red
    '#34495E', // Dark slate
    '#27AE60', // Green
    '#8E44AD', // Dark purple
    '#D35400', // Dark orange
    '#16A085', // Sea green
    '#C0392B', // Dark red
    '#2980B9', // Light blue
    '#F39C12', // Golden
    '#1ABC9C', // Turquoise
    '#E67E22' // Carrot
  ];

  if (numColours <= baseColours.length) {
    return baseColours.slice(0, numColours);
  }

  // For additional colours beyond the base palette, generate new colours
  // using HSL colour space for better control over distinctiveness
  return Array.from({ length: numColours }, (_, i) => {
    if (i < baseColours.length) {
      return baseColours[i];
    }

    // Use golden ratio for hue distribution (ensures good spacing)
    const goldenRatio = 0.618033988749895;
    const hue = (i * goldenRatio * 360) % 360;

    // Alternate between different saturation/lightness combinations
    // to maintain distinctiveness
    const combinations = [
      { s: 70, l: 45 }, // Rich, medium
      { s: 85, l: 60 }, // Bright, lighter
      { s: 60, l: 35 }, // Muted, darker
      { s: 75, l: 55 }, // Balanced, medium-light
      { s: 65, l: 40 } // Moderate, medium-dark
    ];

    const { s, l } = combinations[i % combinations.length];

    return `hsl(${hue}, ${s}%, ${l}%)`;
  });
};

export default generateColours;
